import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ApiService } from '../../../services/api';
import { ControlPogResetMappingRun } from '../../../types';

const RUN_POLL_INTERVAL_MS = 5000;
const MAX_POLL_FAILURES = 3;

type UsePogResetMappingRunOptions = {
  storeName: string | undefined;
  /** Called after a run reaches a terminal status, to refresh whatever the page shows. */
  onRunComplete: () => void | Promise<void>;
};

export type PogResetMappingRunController = {
  activeRun: ControlPogResetMappingRun | null;
  isRunning: boolean;
  isTriggering: boolean;
  /** Seeds the run being polled from a page's own task fetch (survives a mid-run reload). */
  syncActiveRun: (run: ControlPogResetMappingRun | null | undefined) => void;
  start: (force: boolean, tagPrefix?: string, skipPr?: boolean) => Promise<void>;
};

export const usePogResetMappingRun = ({
  storeName,
  onRunComplete,
}: UsePogResetMappingRunOptions): PogResetMappingRunController => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  const [activeRun, setActiveRun] = useState<ControlPogResetMappingRun | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const pollFailuresRef = useRef(0);

  // Kept in a ref so the polling effect never restarts when the caller passes a
  // fresh closure on every render.
  const onRunCompleteRef = useRef(onRunComplete);
  onRunCompleteRef.current = onRunComplete;

  const syncActiveRun = useCallback((run: ControlPogResetMappingRun | null | undefined) => {
    setActiveRun(run?.status === 'running' ? run : null);
  }, []);

  useEffect(() => {
    if (!storeName || !activeRun || activeRun.status !== 'running') {
      return undefined;
    }

    const runId = activeRun.runId;
    pollFailuresRef.current = 0;
    const intervalId = window.setInterval(async () => {
      try {
        const run = await ApiService.fetchPogResetMappingRun(token, storeName, runId);
        pollFailuresRef.current = 0;
        if (run.status === 'completed') {
          setActiveRun(null);
          showSuccessToast('Mapping calculation completed');
          await onRunCompleteRef.current();
        } else if (run.status === 'failed') {
          setActiveRun(null);
          showErrorToast(`Mapping calculation failed: ${run.error ?? 'unknown error'}`);
          await onRunCompleteRef.current();
        }
      } catch (err) {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
          setActiveRun(null);
          showErrorToast(
            err instanceof Error ? err.message : 'Lost track of the mapping calculation run'
          );
        }
      }
    }, RUN_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?.runId, activeRun?.status, storeName, token]);

  const start = async (force: boolean, tagPrefix?: string, skipPr?: boolean) => {
    if (!storeName || isTriggering) {
      return;
    }

    setIsTriggering(true);
    try {
      const run = await ApiService.startPogResetMappingRun(token, storeName, {
        force,
        tagPrefix,
        skipPr,
      });
      if (run.status === 'running') {
        setActiveRun(run);
        showSuccessToast(
          'Mapping calculation started — it runs in the background, results will appear here in a few minutes'
        );
      } else {
        // Cached-only runs can complete before the response returns.
        await onRunCompleteRef.current();
        if (run.status === 'failed') {
          showErrorToast(`Mapping calculation failed: ${run.error ?? 'unknown error'}`);
        } else {
          showSuccessToast('Mapping calculation completed');
        }
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error starting mapping calculation');
    } finally {
      setIsTriggering(false);
    }
  };

  return {
    activeRun,
    isRunning: activeRun?.status === 'running',
    isTriggering,
    syncActiveRun,
    start,
  };
};
